import {
  NOTIFICATION_PRODUCT_LABELS,
  NotificationProductCode,
  PermissionCode,
  notificationProductCodeSchema,
  type AppNotification,
} from '@gb-ticket/shared-types';
import { CheckIcon, ChevronRightIcon, InboxIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListPagination } from '@/components/list-pagination';
import { notificationTarget } from '@/features/notifications/notification-target';
import {
  useDeleteAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationInbox,
  type NotificationInboxFilter,
} from '@/features/notifications/use-notifications';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { hasPermission } from '@/platform/auth/permissions';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';

export function NotificationsPage() {
  usePageTitle('Mitteilungen');
  const copy = useVoiceCopy();
  const me = useMe();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationInboxFilter>({
    unreadOnly: false,
    productCode: null,
    page: 1,
  });
  const inbox = useNotificationInbox(filter);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const remove = useDeleteNotification();
  const removeAll = useDeleteAllNotifications();
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const items = inbox.data?.items ?? [];
  const total = inbox.data?.total ?? 0;
  const unread = inbox.data?.unreadCount ?? 0;
  const audience = hasPermission(me.data, PermissionCode.TICKETS_READ_ALL) ? 'agent' : 'portal';
  const moduleOptions: NotificationProductCode[] = [
    NotificationProductCode.PLATFORM,
    ...(me.data?.products ?? []),
  ];
  const filtered = filter.unreadOnly || filter.productCode !== null;
  const canDeleteAll = inbox.data !== undefined && (total > 0 || items.length > 0 || unread > 0);

  function patchFilter(patch: Partial<Omit<NotificationInboxFilter, 'page'>>) {
    setFilter((current) => ({ ...current, ...patch, page: 1 }));
  }

  function open(item: AppNotification) {
    if (!item.readAt) {
      void markRead.mutateAsync(item.id);
    }
    const target = notificationTarget(item, audience);
    if (target) {
      void navigate(target);
    }
  }

  async function confirmDeleteAll() {
    try {
      const result = await removeAll.mutateAsync();
      setDeleteAllOpen(false);
      setFilter({ unreadOnly: false, productCode: null, page: 1 });
      toast.success(
        result.deleted === 1
          ? 'Eine Benachrichtigung gelöscht.'
          : `${result.deleted} Benachrichtigungen gelöscht.`,
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Benachrichtigungen konnten nicht gelöscht werden.',
      );
    }
  }

  async function removeItem(id: string) {
    try {
      await remove.mutateAsync(id);
      toast.success('Benachrichtigung gelöscht.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Benachrichtigung konnte nicht gelöscht werden.',
      );
    }
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <PlatformPageHeader
        title="Mitteilungen"
        description={
          !inbox.data
            ? copy('platform.notifications.yours')
            : unread > 0
              ? `${unread} ungelesen`
              : 'Alles gelesen.'
        }
        action={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => void markAll.mutateAsync()}
            >
              {markAll.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : (
                <CheckIcon aria-hidden />
              )}
              Alle gelesen
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={!canDeleteAll || removeAll.isPending}
              onClick={() => setDeleteAllOpen(true)}
            >
              {removeAll.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2Icon aria-hidden />
              )}
              Alle löschen
            </Button>
          </div>
        }
      />

      <ConfirmDeleteDialog
        open={deleteAllOpen}
        title="Alle Benachrichtigungen löschen?"
        description={copy('platform.notifications.delete-all')}
        pending={removeAll.isPending}
        onCancel={() => setDeleteAllOpen(false)}
        onConfirm={() => void confirmDeleteAll()}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
        <div
          role="group"
          aria-label="Anzeige"
          className="inline-flex rounded-lg border border-border bg-muted/30 p-0.5"
        >
          {[
            { value: false, label: 'Alle' },
            { value: true, label: 'Ungelesen' },
          ].map((option) => (
            <Button
              key={option.label}
              type="button"
              size="sm"
              variant={filter.unreadOnly === option.value ? 'secondary' : 'ghost'}
              aria-pressed={filter.unreadOnly === option.value}
              className="min-h-9 gap-1.5"
              onClick={() => patchFilter({ unreadOnly: option.value })}
            >
              {option.label}
              {option.value && unread > 0 ? (
                <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                  {unread > 99 ? '99+' : unread}
                </Badge>
              ) : null}
            </Button>
          ))}
        </div>
        <label className="flex min-w-0 flex-1 items-center gap-2 text-sm sm:max-w-xs sm:flex-none">
          <span className="shrink-0 text-muted-foreground">Modul</span>
          <Select
            value={filter.productCode ?? 'none'}
            onValueChange={(value) => {
              const parsed = notificationProductCodeSchema.safeParse(value);
              patchFilter({ productCode: parsed.success ? parsed.data : null });
            }}
          >
            <SelectTrigger
              id="notification-module"
              aria-label="Modul"
              className="h-9 min-w-0 flex-1 sm:w-44 sm:flex-none"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Alle Module</SelectItem>
              {moduleOptions.map((code) => (
                <SelectItem key={code} value={code}>
                  {NOTIFICATION_PRODUCT_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        {inbox.data && total > 0 ? (
          <p className="w-full text-xs text-muted-foreground sm:ml-auto sm:w-auto">
            {total === 1 ? '1 Eintrag' : `${total} Einträge`}
            {filtered ? ' (gefiltert)' : null}
          </p>
        ) : null}
      </div>

      {inbox.isPending ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card px-4 py-16 shadow-sm">
          <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Mitteilungen werden geladen …
          </p>
        </div>
      ) : inbox.isError ? (
        <div
          role="alert"
          className="overflow-hidden rounded-xl border border-destructive/30 bg-card px-4 py-8 text-center text-sm text-destructive shadow-sm"
        >
          Die Mitteilungen konnten nicht geladen werden.
        </div>
      ) : items.length === 0 ? (
        <div className="overflow-hidden rounded-xl border border-dashed border-border bg-card px-4 py-16 text-center shadow-sm">
          <InboxIcon className="mx-auto size-10 text-muted-foreground/50" aria-hidden />
          <p className="mt-3 text-sm font-medium">Keine Mitteilungen</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered
              ? 'Für diese Filter gibt es keine Einträge.'
              : 'Neue Hinweise aus Intranet, Helpdesk und anderen Modulen erscheinen hier.'}
          </p>
          {filtered ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setFilter({ unreadOnly: false, productCode: null, page: 1 })}
            >
              Filter zurücksetzen
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  hasTarget={notificationTarget(item, audience) !== null}
                  deletePending={remove.isPending && remove.variables === item.id}
                  onOpen={() => open(item)}
                  onDelete={() => void removeItem(item.id)}
                />
              ))}
            </ul>
          </div>
          {inbox.data ? (
            <ListPagination
              page={inbox.data.page}
              pageSize={inbox.data.pageSize}
              total={inbox.data.total}
              onPageChange={(page) => setFilter((current) => ({ ...current, page }))}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

function NotificationRow({
  item,
  hasTarget,
  deletePending,
  onOpen,
  onDelete,
}: {
  item: AppNotification;
  hasTarget: boolean;
  deletePending: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const unread = !item.readAt;

  return (
    <li className="flex items-stretch">
      <button
        type="button"
        className={cn(
          'group flex min-h-11 min-w-0 flex-1 items-start gap-3 border-l-4 px-4 py-4 text-left transition-colors sm:gap-4 sm:px-5',
          'hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-brand-navy/30 focus-visible:outline-none',
          unread ? 'border-l-brand-navy bg-brand-navy/[0.04]' : 'border-l-transparent',
        )}
        onClick={onOpen}
      >
        <span className="min-w-0 flex-1 space-y-2">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                'text-sm text-primary sm:text-base',
                unread ? 'font-semibold' : 'font-medium',
              )}
            >
              {item.title}
              {unread ? <span className="sr-only"> (ungelesen)</span> : null}
            </span>
            {unread ? (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] uppercase">
                Neu
              </Badge>
            ) : null}
          </span>
          {item.body.trim() ? (
            <span className="line-clamp-2 block text-sm leading-relaxed text-muted-foreground">
              {item.body}
            </span>
          ) : null}
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <Badge variant="outline" className="font-normal text-muted-foreground">
              {NOTIFICATION_PRODUCT_LABELS[item.productCode]}
            </Badge>
            <time dateTime={item.createdAt}>{formatDateTime(item.createdAt)}</time>
          </span>
        </span>
        {hasTarget ? (
          <ChevronRightIcon
            className="mt-0.5 size-5 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
            aria-hidden
          />
        ) : (
          <span className="size-5 shrink-0" aria-hidden />
        )}
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="my-auto mr-2 size-9 shrink-0 text-muted-foreground hover:text-destructive"
        aria-label="Benachrichtigung löschen"
        disabled={deletePending}
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
      >
        {deletePending ? (
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
        ) : (
          <Trash2Icon className="size-4" aria-hidden />
        )}
      </Button>
    </li>
  );
}
